
import { useState } from "react";
import { Card, CardHeader, CardTitle, CardContent, CardFooter } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { useNavigate } from "react-router-dom";
import type { Article } from "@/types/article";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";

const statusOptions = [
  { value: "published", label: "Published" },
  { value: "draft", label: "Draft" },
  { value: "review", label: "In Review" }
];

interface Props {
  initialArticle: Article;
  articleId: string | number;
}

const EditArticleForm = ({ initialArticle, articleId }: Props) => {
  const [article, setArticle] = useState<Article>(initialArticle);
  const [saving, setSaving] = useState(false);
  const navigate = useNavigate();

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    try {
      const { error } = await supabase
        .from("articles")
        .update({
          title: article.title,
          category: article.category,
          description: article.description,
          status: article.status,
          premium: article.premium,
          image: article.image
        })
        .eq("id", String(articleId)); // Convert articleId to string
      
      if (error) {
        console.log("Update error:", error);
        toast.error("Failed to update");
      } else {
        toast.success("Article updated");
        navigate("/admin");
      }
    } catch (err) {
      console.error("Error updating article:", err);
      toast.error("Failed to update");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card className="w-full max-w-lg">
      <CardHeader>
        <CardTitle>Edit Article</CardTitle>
      </CardHeader>
      <form onSubmit={handleSave}>
        <CardContent className="space-y-3">
          <div className="space-y-2">
            <Label htmlFor="title">Title</Label>
            <Input
              id="title"
              placeholder="Title"
              value={article.title || ""}
              onChange={e => setArticle({ ...article, title: e.target.value })}
              required
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="category">Category</Label>
            <Input
              id="category"
              placeholder="Category"
              value={article.category || ""}
              onChange={e => setArticle({ ...article, category: e.target.value })}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="description">Description</Label>
            <Textarea
              id="description"
              placeholder="Description"
              className="min-h-[100px]"
              value={article.description || ""}
              onChange={e =>
                setArticle({ ...article, description: e.target.value })
              }
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="image">Header Image URL</Label>
            <Input
              id="image"
              placeholder="Image URL"
              value={article.image || ""}
              onChange={e => setArticle({ ...article, image: e.target.value })}
            />
            {article.image && (
              <div className="mt-2 rounded-md overflow-hidden h-32 bg-gray-100">
                <img 
                  src={article.image} 
                  alt="Article header" 
                  className="w-full h-full object-cover"
                  onError={(e) => {
                    e.currentTarget.src = "https://images.unsplash.com/photo-1486312338219-ce68d2c6f44d";
                  }}
                />
              </div>
            )}
          </div>
          <div className="flex items-center space-x-2">
            <Switch
              id="premium"
              checked={article.premium || false}
              onCheckedChange={(checked) => 
                setArticle({ ...article, premium: checked })
              }
            />
            <Label htmlFor="premium">Premium Content (Paywall)</Label>
          </div>
          <div className="space-y-2">
            <Label htmlFor="status">Status</Label>
            <select
              id="status"
              className="w-full h-10 rounded-md border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-ai-indigo"
              value={article.status || "draft"}
              onChange={e =>
                setArticle({ ...article, status: e.target.value })
              }
            >
              {statusOptions.map(opt => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </div>
        </CardContent>
        <CardFooter className="justify-end gap-2">
          <Button
            type="button"
            variant="outline"
            onClick={() => navigate("/admin")}
            disabled={saving}
          >
            Cancel
          </Button>
          <Button type="submit" className="bg-ai-indigo" disabled={saving}>
            {saving ? "Saving…" : "Save"}
          </Button>
        </CardFooter>
      </form>
    </Card>
  );
};

export default EditArticleForm;
